import $ from 'jquery';
import { Modal } from 'bootstrap';
import Swiper, { type Swiper as SwiperInstance } from 'swiper';
import type { SwiperOptions } from 'swiper/types';
import { FreeMode, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export default class Global {
    static getCurrentTenant(): string {
        const tenantInput = document.getElementById('Tenant') as HTMLInputElement | null;
        return tenantInput?.value || '';
    }

    // Helper to get CSRF token
    static getToken = (): string => {
        const input = document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]');
        return input?.value || '';
    };

    static showError(selector: string, message?: string): void {
        console.log(selector);
        console.log(message);
        const el = document.querySelector<HTMLElement>(selector);
        if (!el) {
            console.log("element not found");
            return;
        }

        if (message) el.textContent = message;
        el.style.display = "block";
    };

    static hideError(selector: string) : void {
        const el = document.querySelector<HTMLElement>(selector);
        if (!el) return;

        el.style.display = "none";
    };

    static addInputError = (selector: string) => document.querySelector<HTMLElement>(selector)?.classList.add('input-error');
    static removeInputError = (selector: string) => document.querySelector<HTMLElement>(selector)?.classList.remove('input-error');

    static imgError = (e: HTMLImageElement, fallback: string): void => {
        e.onerror = null; // prevent infinite loop
        e.src = fallback;
    };

    static dateDiffInDays(date1: Date, date2: Date): number {
        const utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate());
        const utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate());
        return Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
    }

    static getDaysInMonth = (year: number, month: number): number => {
        return new Date(year, month, 0).getDate();
    };

    static elementInView($el: JQuery<HTMLElement>, preload: number = 100): boolean {
        const rect = $el[0].getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;

        return (
            rect.top <= windowHeight + preload &&
            rect.left <= windowWidth + preload &&
            rect.bottom >= -preload &&
            rect.right >= -preload
        );
    }

    static checkLazyImages(): void {
        $('.lazy-load-background-image').each((_, el) => {
            const $el = $(el);
            if (Global.elementInView($el)) {
                if ($el.css('background-image') === 'none') {
                    $el.css('background-image', $el.data('background-style'));
                }
            }
        });

        $('.lazy-load-image').each((_, el) => {
            const $el = $(el);
            const src = $el.attr('src') || '';
            if (Global.elementInView($el)) {
                if (src === '' || src.includes('noimage.jpg')) {
                    $el.attr('src', $el.data('img-src'));
                }
            }
        });
    }

    /**
     * Open any modal via buttons with `data-modal-target="#modalId"` attribute
     */
    static initGlobalModalTriggers(): void {
        document.addEventListener('click', (event) => {
            const target = event.target as HTMLElement;
            const trigger = target.closest('[data-modal-target]') as HTMLElement | null;
            if (!trigger) return;

            event.preventDefault();

            const modalId = trigger.getAttribute('data-modal-target');
            if (!modalId) return;

            const modalElement = document.querySelector<HTMLElement>(modalId);
            if (!modalElement) return;

            // Use existing Bootstrap instance or create a new one
            const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
            modalInstance.show();
        });
    }

    /**
     * Global handler to close any modal with a `.modal-close` button
     */
    static initGlobalModalClose(): void {
        document.addEventListener('click', (event) => {
            const target = event.target as HTMLElement;
            const closeButton = target.closest('.modal-close') as HTMLElement | null;
            
            if (!closeButton) return;

            // Try Bootstrap modal first
            const bootstrapModal = closeButton.closest('.modal') as HTMLElement | null;
            if (bootstrapModal) {
                const instance = Modal.getInstance(bootstrapModal);
                if (instance) {
                    instance.hide(); // this will remove backdrop and modal-open automatically
                    return;
                }
            }

            // Fallback for custom modals
            const customModal = closeButton.closest<HTMLElement>('.custom-modal, .hidden-modal, .db-hotel-gallery-modal, #cancelBookingModal');
            if (customModal) {
                customModal.classList.add('hidden');

                // Remove any lingering Bootstrap backdrop
                document.querySelectorAll('.modal-backdrop').forEach(b => b.remove());
                document.body.classList.remove('modal-open');
            }
        });
    }

    static initCounters(): void {
        const updateCounterAriaLabels = () => {
            $(".db-counter").each(function () {
                const itemType = $(this).data("item-type") as string || "item";

                const addBtn = $(this).find(".db-counter-add");
                const removeBtn = $(this).find(".db-counter-remove");

                if (addBtn.length) {
                    addBtn.attr("aria-label", `Add ${itemType}`);
                    addBtn.attr("role", "button");
                    addBtn.attr("tabindex", "0");
                }

                if (removeBtn.length) {
                    removeBtn.attr("aria-label", `Remove ${itemType}`);
                    removeBtn.attr("role", "button");
                    removeBtn.attr("tabindex", "0");
                }
            });
        };

        // Initialize labels first
        updateCounterAriaLabels();

        // Counter Add Generic
        $(document).on("click", ".db-counter-add", function (e) {
            e.preventDefault();

            const display = $(this).siblings(".db-counter-value");
            const ref = display.data("model-ref") as string;

            const max = parseInt(String($("#" + ref).data("max-value") ?? "0"), 10);
            const cur = parseInt(String($("#" + ref).val() ?? "0"), 10);

            if (cur + 1 <= max) {
                $("#" + ref).val(cur + 1);
                display.html(String(cur + 1));
                $(this).siblings(".db-counter-remove").removeClass("db-counter-button-disable");
            }

            const newCur = parseInt(String($("#" + ref).val() ?? "0"), 10);
            if (newCur === max) {
                $(this).addClass("db-counter-button-disable");
            } else {
                $(this).removeClass("db-counter-button-disable");
            }

            // Trigger custom event AFTER global logic
            const $button = $(this);
            const event = new CustomEvent("db-counter-updated", {
                detail: {
                    buttonRef: $button,
                    value: newCur
                }
            });
            document.dispatchEvent(event);
        });

        // Counter Remove Generic
        $(document).on("click", ".db-counter-remove", function (e) {
            e.preventDefault();

            const display = $(this).siblings(".db-counter-value");
            const ref = display.data("model-ref") as string;

            const min = parseInt(String($("#" + ref).data("min-value") ?? "0"), 10);
            const cur = parseInt(String($("#" + ref).val() ?? "0"), 10);

            if (cur - 1 >= min) {
                $("#" + ref).val(cur - 1);
                display.html(String(cur - 1));
                $(this).siblings(".db-counter-add").removeClass("db-counter-button-disable");
            }

            const newCur = parseInt(String($("#" + ref).val() ?? "0"), 10);
            if (newCur === min) {
                $(this).addClass("db-counter-button-disable");
            } else {
                $(this).removeClass("db-counter-button-disable");
            }

            // Trigger custom event AFTER global logic
            const $button = $(this);
            const event = new CustomEvent("db-counter-updated", {
                detail: {
                    buttonRef: $button,
                    value: newCur
                }
            });
            document.dispatchEvent(event);
        });
    }

    static formatNumber(value: number, numDecimals: number, includeComma: boolean): string {
        return value.toLocaleString("en-US", {
            useGrouping: includeComma,
            minimumFractionDigits: numDecimals,
            maximumFractionDigits: numDecimals
        });
    }

    static getAntiForgeryToken(): string {
        const tokenInput = document.querySelector<HTMLInputElement>(
            'input[name="__RequestVerificationToken"]'
        );
        return tokenInput?.value || "";
    }

    static getSwiperConfig(
        container: HTMLElement,
        customNav = true,
        extraConfig: Partial<SwiperOptions> = {}
    ): SwiperOptions {
        const prevBtn = container.querySelector('.swiper-prev') as HTMLElement | null;
        const nextBtn = container.querySelector('.swiper-next') as HTMLElement | null;

        const setupFadeButtons = (container: HTMLElement, prevBtn: HTMLElement, nextBtn: HTMLElement) => {
            const showButtons = () => {
                prevBtn.classList.add('visible');
                nextBtn.classList.add('visible');
            };

            const hideButtons = () => {
                if (!prevBtn.classList.contains('swiper-button-disabled')) {
                    prevBtn.classList.remove('visible');
                }
                if (!nextBtn.classList.contains('swiper-button-disabled')) {
                    nextBtn.classList.remove('visible');
                }
            };

            // Show on hover or touch
            container.addEventListener('mouseenter', showButtons);
            container.addEventListener('mousemove', showButtons);
            container.addEventListener('touchstart', showButtons, { passive: true });

            // Hide when leaving container
            container.addEventListener('mouseleave', hideButtons);
        };

        const config: SwiperOptions = {
            modules: [Navigation, Pagination],
            slidesPerView: 1.7,
            spaceBetween: 12,
            pagination: {
                el: container.querySelector('.swiper-pagination') as HTMLElement,
                clickable: true
            },
            on: {
                init(this: SwiperInstance) {
                    if (prevBtn && nextBtn) {
                        // Add ARIA labels for screen readers
                        prevBtn.setAttribute('aria-label', 'Previous slide');
                        nextBtn.setAttribute('aria-label', 'Next slide');

                        // Optional: give them a role (if they’re not <button>)
                        if (prevBtn.tagName.toLowerCase() !== 'button') {
                            prevBtn.setAttribute('role', 'button');
                            prevBtn.setAttribute('tabindex', '0');
                        }
                        if (nextBtn.tagName.toLowerCase() !== 'button') {
                            nextBtn.setAttribute('role', 'button');
                            nextBtn.setAttribute('tabindex', '0');
                        }

                        prevBtn.addEventListener('click', () => this.slidePrev());
                        nextBtn.addEventListener('click', () => this.slideNext());
                        setupFadeButtons(container, prevBtn, nextBtn);
                    }

                    const updateButtons = () => {
                        prevBtn?.classList.toggle('swiper-button-disabled', this.isBeginning);
                        nextBtn?.classList.toggle('swiper-button-disabled', this.isEnd);
                    };

                    updateButtons();
                    this.on('slideChange', updateButtons);
                    this.on('reachEnd', updateButtons);
                    this.on('reachBeginning', updateButtons);
                    this.on('fromEdge', updateButtons);
                    this.on('resize', updateButtons);
                },
                breakpoint(swiper) {
                    swiper.slideTo(0);
                },
                touchMove() {
                    Global.checkLazyImages();
                },
                slideChange() {
                    Global.checkLazyImages();
                },
                slideChangeTransitionEnd() {
                    Global.checkLazyImages();
                },
                navigationNext() {
                    Global.checkLazyImages();
                },
                navigationPrev() {
                    Global.checkLazyImages();
                },
                slideNextTransitionStart() {
                    Global.checkLazyImages();
                },
                slideNextTransitionEnd() {
                    Global.checkLazyImages();
                }
            },
            ...extraConfig
        };

        return config;
    }

    static initHotelListSwiper(): void {
        const hotelSwiperContainers = document.querySelectorAll<HTMLElement>('.hotel-list-block-swiper');

        if (hotelSwiperContainers.length === 0) return;

        hotelSwiperContainers.forEach((container) => {
            const swiper = new Swiper(container, Global.getSwiperConfig(container, true, {
                slidesOffsetAfter: 15,
                breakpoints: {
                    520: { slidesPerView: 2.3, spaceBetween: 16  },
                    768: { slidesPerView: 2.8, spaceBetween: 16 },
                    992: { slidesPerView: 3.7, spaceBetween: 20  },
                    1200: { slidesPerView: 5, spaceBetween: 20, slidesOffsetAfter: 0 }
                }
            }));
        });
    }

    static initTabsSwiper(): void {
        const tabSwiperEl = document.querySelectorAll<HTMLElement>('.db-theme-tabs.swiper');

        if (tabSwiperEl.length === 0) return;

        tabSwiperEl.forEach((container) => {
            const swiper = new Swiper(container, Global.getSwiperConfig(container, true, {
                modules: [FreeMode],
                slidesPerView: 'auto',
                spaceBetween: 8,
                freeMode: true,
                observer: true,
                observeParents: true
            }));
        });
    };

    static initThemeHotelSwipers(): void {
        const groups = document.querySelectorAll<HTMLElement>('.db-theme-swiper-group');
        if (groups.length === 0) return;

        groups.forEach((group) => {
            const tabs = group.querySelectorAll<HTMLElement>('.db-theme-tab');
            const swipers = group.querySelectorAll<HTMLElement>('.db-theme-swiper-container');

            // Keep track of initialized Swipers
            const swiperInstances: Record<string, SwiperInstance> = {};

            // Initialize only visible swiper containers
            swipers.forEach((swiperContainer) => {
                const swiperEl = swiperContainer.querySelector<HTMLElement>('.db-theme-hotel-swiper');
                if (!swiperEl) return;

                if (swiperContainer.classList.contains('active')) {
                    swiperInstances[swiperContainer.dataset.theme || 'default'] = new Swiper(swiperEl, Global.getSwiperConfig(swiperEl, true, {
                        slidesOffsetAfter: 15,
                        breakpoints: {
                            520: { slidesPerView: 2.3, spaceBetween: 16 },
                            768: { slidesPerView: 2.8, spaceBetween: 16 },
                            992: { slidesPerView: 3.7, spaceBetween: 20 },
                            1200: { slidesPerView: 5, spaceBetween: 20, slidesOffsetAfter: 0 }
                        }
                    }));
                }
            });

            // Handle tab switching
            tabs.forEach((tab) => {
                tab.addEventListener('click', () => {
                    const theme = tab.getAttribute('data-theme');
                    if (!theme) return;

                    // Activate the tab
                    tabs.forEach(t => t.classList.remove('active'));
                    tab.classList.add('active');

                    // Activate the corresponding swiper container
                    swipers.forEach(swiperContainer => {
                        const isMatch = swiperContainer.getAttribute('data-theme') === theme;
                        swiperContainer.classList.toggle('active', isMatch);

                        const swiperEl = swiperContainer.querySelector<HTMLElement>('.db-theme-hotel-swiper');
                        if (!swiperEl) return;

                        // Lazy initialize if not already
                        if (isMatch && !swiperInstances[theme]) {
                            swiperInstances[theme] = new Swiper(swiperEl, Global.getSwiperConfig(swiperEl, true, {
                                slidesOffsetAfter: 15,
                                breakpoints: {
                                    520: { slidesPerView: 2.3, spaceBetween: 16 },
                                    768: { slidesPerView: 2.8, spaceBetween: 16 },
                                    992: { slidesPerView: 3.7, spaceBetween: 20 },
                                    1200: { slidesPerView: 5, spaceBetween: 20, slidesOffsetAfter: 0 }
                                }
                            }));
                        }

                        // Force update to recalc widths when becoming visible
                        if (isMatch && swiperInstances[theme]) {
                            swiperInstances[theme].update();
                        }
                    });
                });
            });

            // Also force update on window resize (important for mobile fractional slides)
            window.addEventListener('resize', () => {
                Object.values(swiperInstances).forEach(swiper => swiper.update());
            });
        });
    }

    static initLocationListSwiper(): void {
        const locationSwiperEl = document.querySelectorAll<HTMLElement>('.location-list-block-swiper');

        if (locationSwiperEl.length === 0) return;

        locationSwiperEl.forEach((container) => {
            const swiper = new Swiper(container, Global.getSwiperConfig(container, true, {
                slidesPerView: 1.7,
                spaceBetween: 12,
                slidesOffsetAfter: 15,
                breakpoints: {
                    520: { slidesPerView: 2.3, spaceBetween: 16 },
                    768: { slidesPerView: 2.5, spaceBetween: 16 },
                    992: { slidesPerView: 2.8, spaceBetween: 20 },
                    1200: { slidesPerView: 5, spaceBetween: 20, slidesOffsetAfter: 0 }
                }
            }));
        });
    }

    static initRecentPostsSwiper(): void {
        const container = document.querySelector<HTMLElement>('.blog-post-swiper');

        if (!container) {
            console.warn('Recent posts swiper container not found');
            return;
        }

        const swiper = new Swiper(container, Global.getSwiperConfig(container, true, {
            slidesPerView: 1.3,
            spaceBetween: 12,
            slidesOffsetAfter: 15,
            breakpoints: {
                768: { slidesPerView: 1.8, spaceBetween: 16 },
                992: { slidesPerView: 2.5, spaceBetween: 20 },
                1200: { slidesPerView: 3, spaceBetween: 20, slidesOffsetAfter: 0 }
            }
        }));
    }

    /**
     * Hides body overflow on mobile devices below the specified breakpoint.
     * @param breakpoint Optional mobile breakpoint in pixels (default: 768)
     */
    static hideOverflowMobile(breakpoint: number = 768): void {
        const applyOverflow = () => {
            if (window.innerWidth < breakpoint) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        };

        // Apply immediately
        applyOverflow();

        // Re-apply on resize (for orientation changes)
        window.addEventListener('resize', applyOverflow);
    }

    static updateFooterLinkGrid(containerSelector: string) {
        const container = document.querySelector(containerSelector) as HTMLElement | null;
        if (!container) return;

        const items = container.querySelectorAll('.primary-link');
        const total = items.length;
        if (total === 0) return;

        const width = window.innerWidth;
        let columns = 1;

        // Determine number of columns based on device width
        if (width >= 1200) {        // desktop
            columns = 4;
        } else if (width >= 992) {  // laptop
            columns = 3;
        } else if (width >= 768) {  // tablet
            columns = 2;
        } else {
            columns = 1;            // mobile
        }

        const rows = Math.ceil(total / columns);

        // Apply dynamic grid layout
        container.style.display = 'grid';
        container.style.gridTemplateColumns = `repeat(${columns}, 1fr)`;
        container.style.gridTemplateRows = `repeat(${rows}, auto)`;
        container.style.gridAutoFlow = 'column';
    }
}

(window as any).Global = Global;